import { useEffect, useState } from "react";
import api from "@/api/client";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function AnalyticsTab({ space }) {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [topArticles, setTopArticles] = useState([]);
  const [topSearches, setTopSearches] = useState([]);

  useEffect(() => {
    api.get(`/spaces/${space.id}/analytics?days=${days}`).then((r) => setSummary(r.data));
    api.get(`/spaces/${space.id}/analytics/articles?days=${days}`).then((r) => setTopArticles(r.data));
    api.get(`/spaces/${space.id}/analytics/searches?days=${days}`).then((r) => setTopSearches(r.data));
  }, [space.id, days]);

  if (!summary) return <div className="p-6 text-zinc-500">Loading…</div>;

  const cards = [
    { label: "Total views", value: summary.total_views },
    { label: "Unique visitors", value: summary.unique_visitors },
    { label: "Searches", value: summary.searches },
    { label: "Helpful %", value: `${summary.helpful_pct}%` },
  ];

  return (
    <div className="p-6 max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black tracking-tight">Analytics</h2>
        <div className="flex gap-1 border border-zinc-300 rounded-sm overflow-hidden">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`range-${d}d`}
              className={`px-3 py-1.5 text-xs font-semibold ${days === d ? "bg-zinc-900 text-white" : "bg-white hover:bg-zinc-50"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold">{c.label}</div>
            <div className="text-3xl font-black tracking-tight mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-5">
        <h3 className="font-bold mb-4">Views over time</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={summary.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="date" fontSize={11} stroke="#71717a" />
              <YAxis fontSize={11} stroke="#71717a" />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }} />
              <Line type="monotone" dataKey="views" stroke="#41B5E6" strokeWidth={2} dot={{ fill: "#41B5E6", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="p-4 border-b font-bold">Top articles</div>
          <div className="divide-y divide-zinc-100">
            {topArticles.length === 0 ? (
              <div className="p-6 text-sm text-zinc-500 text-center">No data yet</div>
            ) : topArticles.map((a) => (
              <div key={a.slug} className="p-3 flex items-center justify-between text-sm">
                <span className="truncate flex-1">{a.title}</span>
                <span className="text-xs font-bold text-zinc-700">{a.views} views</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="p-4 border-b font-bold">Top searches</div>
          <div className="divide-y divide-zinc-100">
            {topSearches.length === 0 ? (
              <div className="p-6 text-sm text-zinc-500 text-center">No searches yet</div>
            ) : topSearches.map((s) => (
              <div key={s.query} className="p-3 flex items-center justify-between text-sm">
                <span className="truncate flex-1 font-mono">{s.query}</span>
                <span className="text-xs font-bold text-zinc-700">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
